import { Injectable, OnDestroy } from '@angular/core';
import { map, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';

import { MonitoringLeftSidebarService } from './monitoring-left-sidebar.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import {
    AlertTypeLhpItem,
    BriefingDeliveryMethodLhpItem,
    GroupedAuditableModel,
    ItemRow,
    LeftSidebarManagementService,
    LhpManagementConfig,
    StatusLhpItem,
    TypeLhpItem
} from '../../../Common/Layout/services/left-sidebar-management.service';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeAutoTaggingTopicsCheckboxModel } from '../../../Common/Filter/models/filter-type-auto-tagging-topics-checkbox.model';
import {
    AutoTaggingTopicsCheckboxFilterComponent
} from '../../../Common/Filter/components/AutoTaggingTopicsCheckboxFilter/auto-tagging-topics-checkbox-filter.component';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TopicsService } from './topics.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { FolderService } from './folder.service';
import { AlertsService } from '../../Alerts/services/alerts.service';
import { BriefingsService } from '../../Briefings/services/briefings.service';
import { MediaSubscriptionService } from './media-subscription.service';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { TableColumnInputEnum } from '../../../Common/Forms/enums/table-column-input-type.enum';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';
import { MediaSubscriptionStatusEnum } from '../../Shared/enums/media-subscription-status.enum';
import { allowedMediaSubscriptionEnum } from '../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { TableColumnModel } from '../../../Common/Forms/models/table-column.model';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { AlertStatusEnum, AlertTypeEnum } from '../../../Common/MonitorShared/enum/alert-type.enum';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { BriefingDeliveryMethodEnum } from '../../Briefings/enums/briefing-delivery-method.enum';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';

type AutoTaggingLhpItem = GroupedAuditableModel & {applyTagIds: number[]};
type CountLhpItem = GroupedAuditableModel & {totalMentions: number};

@Injectable()
export class MonitoringLeftSidebarManagementService extends LeftSidebarManagementService implements OnDestroy {
    private readonly typeFilter = new FilterTypeCheckboxModel<string>(
        'type',
        'Type',
        [],
        true
    );

    private readonly autoTaggingFilter = new FilterTypeAutoTaggingTopicsCheckboxModel(
        'autotags',
        'Monitor.leftSidebar.topics.filter.autoTag',
        AutoTaggingTopicsCheckboxFilterComponent,
        [],
        undefined,
        true
    );

    private readonly alertTypeFilter = new FilterTypeCheckboxModel<string>(
        'alertType',
        'Alerts.alertType',
        [],
        true
    );

    private readonly briefingDeliveryMethodFilter = new FilterTypeCheckboxModel<string>(
        'deliveryMethod',
        'Monitor.leftSidebar.briefings.type',
        [],
        true
    );

    private autoTaggingCustomColumnModel: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Common.lhp.manageList.dialog.column.autoTags',
        field: 'autoTags',
        width: 100,
        customTemplate: null
    };

    private modeConfigMap: Map<Mode, LhpManagementConfig> = new Map<Mode, LhpManagementConfig>([
        [
            Mode.topics,
            {
                itemSource: this.topicsService.getTopics(),
                columns: [
                    this.itemNameColumn,
                    ...this.metadataColumns,
                    this.autoTaggingCustomColumnModel
                ],
                customFilters: [this.autoTaggingFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getTagFilterOptions(this.autoTaggingFilter, this.allListItems, this.getAutoTagIdsFromTopic);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyTagsFilter(
                        this.autoTaggingFilter,
                        filteredItems,
                        this.sidebarService.getAutoTagIdsFromTopic,
                        this.sidebarService.getTopicsWithAutoTagging
                    );
                },
                resetCustomFilters: () => {
                    this.autoTaggingFilter.setValue([]);
                }
            }
        ],
        [
            Mode.newSearch,
            {
                itemSource: this.topicsService.getTopics(),
                columns: [
                    this.itemNameColumn,
                    this.autoTaggingCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.autoTaggingFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getTagFilterOptions(this.autoTaggingFilter, this.allListItems, this.getAutoTagIdsFromTopic);
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyTagsFilter(
                        this.autoTaggingFilter,
                        filteredItems,
                        this.sidebarService.getAutoTagIdsFromTopic,
                        this.sidebarService.getTopicsWithAutoTagging
                    );
                },
                resetCustomFilters: () => {
                    this.autoTaggingFilter.setValue([]);
                }
            }
        ],
        [
            Mode.mentions,
            {
                itemSource: this.folderService.all$,
                columns: [
                    this.itemNameColumn,
                    this.countCustomColumnModel,
                    ...this.metadataColumns
                ]
            }
        ],
        [
            Mode.briefings,
            {
                itemSource: this.briefingsService.getBriefingListItems(),
                columns: [
                    this.itemNameColumn,
                    this.briefingDeliveryMethodCustomColumnModel,
                    this.statusCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.statusFilter, this.briefingDeliveryMethodFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getEnumStringOptions(
                        this.statusFilter,
                        this.allListItems as StatusLhpItem[],
                        (item) => item.status,
                        { prefix: 'Briefings.status.', enumType: BriefingStatusEnum }
                    );
                    this.sidebarService.getEnumStringOptions(
                        this.briefingDeliveryMethodFilter,
                        this.allListItems as BriefingDeliveryMethodLhpItem[],
                        (item) => item.deliveryMethod,
                        { prefix: 'Monitor.leftSidebar.briefings.type.', enumType: BriefingDeliveryMethodEnum }
                    );
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyGenericStringOptions(this.briefingDeliveryMethodFilter, filteredItems, (item) => item['deliveryMethod']);
                },
            }
        ],
        [
            Mode.alerts,
            {
                itemSource: this.alertsService.getAlertsList(),
                columns: [
                    this.itemNameColumn,
                    this.statusCustomColumnModel,
                    this.alertTypeCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.statusFilter, this.alertTypeFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getEnumStringOptions(
                        this.statusFilter,
                        this.allListItems as StatusLhpItem[],
                        (item) => item.status,
                        { prefix: 'Alerts.status.', enumType: AlertStatusEnum }
                    );
                    this.sidebarService.getEnumStringOptions(
                        this.alertTypeFilter,
                        this.allListItems as AlertTypeLhpItem[],
                        (item) => item.alertType,
                        { prefix: 'Alerts.alertType.', enumType: AlertTypeEnum }
                    );
                },
            }
        ],
        [
            Mode.mediaSubscriptions,
            {
                itemSource: this.mediaSubscriptionService.all$,
                columns: [
                    this.itemNameColumn,
                    this.statusCustomColumnModel,
                    ...this.metadataColumns
                ],
                customFilters: [this.statusFilter, this.typeFilter],
                setUpCustomFilters: () => {
                    this.sidebarService.getEnumStringOptions(
                        this.statusFilter,
                        this.allListItems as StatusLhpItem[],
                        (item) => item.status,
                        { prefix: 'MediaSubscriptions.status.', enumType: MediaSubscriptionStatusEnum }
                    );
                    this.sidebarService.getEnumStringOptions(
                        this.typeFilter,
                        this.allListItems as TypeLhpItem[],
                        (item) => item.type,
                        { prefix: 'MediaSubscriptionType.', enumType: allowedMediaSubscriptionEnum}
                    );
                },
                applyCustomFilters: (filteredItems) => {
                    return this.sidebarService.applyTypeFilter(filteredItems as TypeLhpItem[], this.typeFilter);
                },
                resetCustomFilters: () => {
                    this.typeFilter.setValue([]);
                }
            }
        ]
    ]);

    constructor(
        protected usersService: UsersService,
        public sidebarService: MonitoringLeftSidebarService,
        public tagsService: TagsService,
        public topicsService: TopicsService,
        public folderService: FolderService,
        public alertsService: AlertsService,
        public briefingsService: BriefingsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public messageService: MessageService,
        public displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService,
        public coreDialogService: CoreDialogService
    ) {
        super(usersService, sidebarService, displayGroupsService, localizationService);

        this.subscriptions.push(
            this.sidebarService.getMode().pipe(
                switchMap((mode) => {
                    this.currentMode = mode;
                    this.currentConfig = this.modeConfigMap.get(mode);
                    this.setCustomColumnHeadings();
                    this.columnsProvider$.next(this.currentConfig.columns);
                    return this.currentConfig.itemSource;
                }),
                map((items) => {
                    this.allListItems = items;
                })
            ).subscribe(
                () => {
                    this.setUpFilters();
                }
            )
        );

    }

    public setCustomData(rowData: ItemRow[]): ItemRow[] {
        switch (this.currentMode) {
            case Mode.newSearch:
            case Mode.topics:
                this.setAutoTagsData(rowData);
                break;
            case Mode.mentions:
                this.setFolderCountData(rowData);
                break;
            case Mode.briefings:
            case Mode.alerts:
            case Mode.mediaSubscriptions:
                this.setStatusData(rowData);
                break;
        }
        return rowData;
    }

    private setAutoTagsData(rowData: ItemRow[]) {
        rowData.forEach(row => {
            const item = this.allListItems.find(x => x.id === row.original.id) as AutoTaggingLhpItem;
            row.autoTagIds = item.applyTagIds;
            row.autoTags = [];
        });
    }

    private setFolderCountData(rowData: ItemRow[]) {
        rowData.forEach(row => {
            const item = this.allListItems.find(x => x.id === row.original.id) as CountLhpItem;
            row.count = item.totalMentions;
        });
    }

    public handleBulkItemAction(items: AuditableModel[], groups: DisplayGroupModel[], action: ActionsEnum) {
        this.loading$.next(true);
        let service: TopicsService | BriefingsService | AlertsService | MediaSubscriptionService;
        switch(this.currentMode) {
            case Mode.topics:
            case Mode.newSearch:
                service = this.topicsService;
                break;
            case Mode.briefings:
                service = this.briefingsService;
                break;
            case Mode.alerts:
                service = this.alertsService;
                break;
        }

        const ids = items.map(x => x.id);

        switch(action) {
            case ActionsEnum.delete: {
                if (this.currentMode === Mode.mentions) {
                    this.folderService.confirmBulkDelete(items, groups).subscribe((result) => {
                        this.loading$.next(false);
                        this.resetSelectionProvider$.next(result);
                    });
                } else if (this.currentMode === Mode.mediaSubscriptions) {
                    this.mediaSubscriptionService.confirmBulkDelete(items, groups).subscribe((result) => {
                        this.loading$.next(false);
                        this.resetSelectionProvider$.next(result);
                    });
                } else {
                    service.confirmBulkDelete(items, groups).subscribe((result) => {
                        this.loading$.next(false);
                        this.resetSelectionProvider$.next(result);
                    });
                }

                break;
            }
            case ActionsEnum.hide:
            case ActionsEnum.show: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.mentions) {
                    this.folderService.bulkUpdateVisibility(ids, action).subscribe();
                } else if (this.currentMode === Mode.mediaSubscriptions) {
                    this.mediaSubscriptionService.bulkUpdateVisibility(ids, action).subscribe();
                } else {
                    service.bulkUpdateVisibility(ids, action);
                }

                break;
            }
            case ActionsEnum.move: {
                this.displayGroupsService.openGroupSelectionDialog(null, true).subscribe({
                    next: (group: DisplayGroupModel) => {
                        this.resetSelectionProvider$.next(true);

                        if (this.currentMode === Mode.mentions) {
                            // @HACK temp until all services are refactored
                            // eslint-disable-next-line rxjs/no-nested-subscribe
                            this.folderService.updateGroup(ids, group, true).subscribe();
                        } else if (this.currentMode === Mode.mediaSubscriptions) {
                            // @HACK temp until all services are refactored
                            // eslint-disable-next-line rxjs/no-nested-subscribe
                            this.mediaSubscriptionService.updateGroup(ids, group, true).subscribe();
                        } else {
                            service.updateGroup(ids, group);
                        }
                    },
                    complete: () => {
                        this.loading$.next(false);
                    }
                });

                break;
            }
            case ActionsEnum.favorite: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.mentions) {
                    this.folderService.updateFavorites(ids, []).subscribe();
                } else if (this.currentMode === Mode.mediaSubscriptions) {
                    this.mediaSubscriptionService.updateFavorites(ids, []).subscribe();
                } else {
                    service.updateFavorites(ids, []);
                }

                break;
            }
            case ActionsEnum.unfavorite: {
                this.resetSelectionProvider$.next(true);

                if (this.currentMode === Mode.mentions) {
                    this.folderService.updateFavorites([], ids).subscribe();
                } else if (this.currentMode === Mode.mediaSubscriptions) {
                    this.mediaSubscriptionService.updateFavorites([], ids).subscribe();
                } else {
                    service.updateFavorites([], ids);
                }

                break;
            }
        }
    }

    getAutoTagIdsFromTopic(item: GroupedAuditableModel) {
        return item['applyTagIds'];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

}
